import React, { useEffect, useMemo, useRef, useState } from "react";
import { createChart, AreaSeries, HistogramSeries } from "lightweight-charts";

/**
 * PortfolioPositionsChart (final)
 * - Área/linha do valor das posições (mark-to-market)
 * - Micro-barras trades na base (BUY/SELL/BOTH)
 * - Tooltip hover (desktop) + tap (mobile)
 * - NÃO recria chart quando pinned muda (usa pinnedRef)
 * - Título opcional no topo (overlay)
 *
 * Props:
 *  - title?: string
 *  - data: Array<{ date, positions_value, buy_value_day, sell_value_day }>
 *  - todayPoint?: { date, positions_value, buy_value_day, sell_value_day } | null
 *  - currency?: "BRL"
 *  - syncedTimeRange?, onTimeRangeChange?
 */
export default function PortfolioPositionsChart({
  title = "Posições (R$)",
  data,
  todayPoint = null,
  currency = "BRL",
  syncedTimeRange,
  onTimeRangeChange,
}) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);

  const chartRef = useRef(null);
  const posSeriesRef = useRef(null);
  const tradesSeriesRef = useRef(null);

  const dataRef = useRef([]);
  const isSyncingRef = useRef(false);

  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);

  // ✅ refs para não recriar chart por estado
  const pinnedRef = useRef(null);
  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);

  // ✅ solta o tooltip fixo ao tocar fora do componente
    useEffect(() => {
    if (!pinned) return;

    const onDown = (e) => {
        const root = wrapperRef.current;
        if (!root) return;
        // se tocou fora do wrapper, solta
        if (!root.contains(e.target)) setPinned(null);
    };

    // pointerdown cobre mouse + muitos touch; touchstart é extra p/ iOS/Safari
    window.addEventListener("pointerdown", onDown, { capture: true });
    window.addEventListener("touchstart", onDown, { capture: true });

    return () => {
        window.removeEventListener("pointerdown", onDown, { capture: true });
        window.removeEventListener("touchstart", onDown, { capture: true });
    };
    }, [pinned]);

  const COLORS = useMemo(
    () => ({
      grid: "rgba(255,255,255,0.045)",

      line: "rgba(160,210,255,0.95)",
      topFill: "rgba(160,210,255,0.14)",
      botFill: "rgba(160,210,255,0.02)",

      tradeBuy: "rgba(46,125,50,0.22)",
      tradeSell: "rgba(211,47,47,0.22)",
      tradeBoth: "rgba(171,71,188,0.22)",
      tradeNone: "rgba(158,158,158,0.06)",

      tooltipBg: "rgba(10,10,12,0.88)",
      tooltipBorder: "rgba(255,255,255,0.14)",
      tooltipText: "rgba(255,255,255,0.92)",
    }),
    []
  );

  const fmtMoney = useMemo(() => {
    return (x) => {
      if (x == null) return "—";
      const n = Number(x);
      if (!Number.isFinite(n)) return "—";
      try {
        return n.toLocaleString("pt-BR", {
          style: "currency",
          currency,
          maximumFractionDigits: 2,
        });
      } catch {
        return `${n.toFixed(2)}`;
      }
    };
  }, [currency]);

  const formatTime = (t) => {
    if (!t) return "";
    if (typeof t === "string") return t;
    const y = t.year;
    const m = String(t.month).padStart(2, "0");
    const d = String(t.day).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // ======================
  // create chart (ONCE)
  // ======================
  useEffect(() => {
    const container = containerRef.current;
    if (!container || chartRef.current) return;

    const width = container.getBoundingClientRect().width || 600;

    const chart = createChart(container, {
      width,
      height: 200,
      layout: {
        background: { type: "solid", color: "rgba(10,10,12,0.0)" },
        textColor: "rgba(255,255,255,0.78)",
      },
      crosshair: { mode: 1 },
      grid: {
        vertLines: { color: COLORS.grid },
        horzLines: { color: COLORS.grid },
      },

      // ✅ sem labels/escala visível
      rightPriceScale: {
        visible: false,
        borderVisible: false,
        ticksVisible: false,
        scaleMargins: { top: 0.12, bottom: 0.08 },
      },
      leftPriceScale: { visible: false },

      timeScale: {
        borderVisible: false,
        rightOffset: 2,
        fixLeftEdge: true,
        fixRightEdge: true,
        secondsVisible: false,
      },
    });

    const posSeries = chart.addSeries(AreaSeries, {
      priceScaleId: "right",
      lineColor: COLORS.line,
      topColor: COLORS.topFill,
      bottomColor: COLORS.botFill,
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
    });

    const tradesSeries = chart.addSeries(HistogramSeries, {
      priceScaleId: "trades",
      base: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      scaleMargins: { top: 0.90, bottom: 0.0 },
    });
    chart.priceScale("trades").applyOptions({
      visible: false,
      borderVisible: false,
      scaleMargins: { top: 0.90, bottom: 0.0 },
    });

    chartRef.current = chart;
    posSeriesRef.current = posSeries;
    tradesSeriesRef.current = tradesSeries;

    const handleResize = () => {
      if (!chartRef.current || !containerRef.current) return;
      const w = containerRef.current.getBoundingClientRect().width;
      if (w > 0) chartRef.current.applyOptions({ width: w });
    };
    window.addEventListener("resize", handleResize);

    const handleCrosshairMove = (param) => {
      if (pinnedRef.current) return;
      try {
        if (
          !param?.time ||
          !param?.point ||
          !wrapperRef.current ||
          param.point.x < 0 ||
          param.point.y < 0 ||
          param.point.x > wrapperRef.current.clientWidth ||
          param.point.y > wrapperRef.current.clientHeight
        ) {
          setHover(null);
          return;
        }
        const d = formatTime(param.time);
        setHover(dataRef.current.find((r) => r.date === d) || null);
      } catch {
        setHover(null);
      }
    };
    chart.subscribeCrosshairMove(handleCrosshairMove);

    const handleClick = (param) => {
      try {
        if (!param?.time) {
          setPinned(null);
          return;
        }
        const d = formatTime(param.time);
        setPinned(dataRef.current.find((r) => r.date === d) || null);
      } catch {
        setPinned(null);
      }
    };
    chart.subscribeClick(handleClick);

    const handleRangeChange = (range) => {
      if (!onTimeRangeChange || isSyncingRef.current) return;
      if (!range) return;
      onTimeRangeChange(range);
    };
    chart.timeScale().subscribeVisibleTimeRangeChange(handleRangeChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.unsubscribeClick(handleClick);
      chart.timeScale().unsubscribeVisibleTimeRangeChange(handleRangeChange);

      chart.remove();
      chartRef.current = null;
      posSeriesRef.current = null;
      tradesSeriesRef.current = null;
    };
  }, [COLORS, onTimeRangeChange]);

  // ======================
  // set data
  // ======================
  useEffect(() => {
    if (!chartRef.current || !posSeriesRef.current || !tradesSeriesRef.current) return;

    const base = Array.isArray(data) ? data : [];
    const merged = [...base];

    if (todayPoint?.date) {
      const td = String(todayPoint.date);
      const idx = merged.findIndex((x) => String(x?.date) === td);
      const row = { ...todayPoint, date: td };
      if (idx >= 0) merged[idx] = { ...merged[idx], ...row };
      else merged.push(row);
    }

    const clean = merged
      .filter((p) => p?.date)
      .map((p) => {
        const positions_value = p?.positions_value != null ? Number(p.positions_value) : null;
        const buy = p?.buy_value_day != null ? Number(p.buy_value_day) : 0;
        const sell = p?.sell_value_day != null ? Number(p.sell_value_day) : 0;

        return {
          date: String(p.date),
          positions_value: Number.isFinite(positions_value) ? positions_value : null,
          buy_value_day: Number.isFinite(buy) ? buy : 0,
          sell_value_day: Number.isFinite(sell) ? sell : 0,
        };
      })
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    dataRef.current = clean;

    const posData = clean
      .filter((p) => Number.isFinite(p.positions_value))
      .map((p) => ({ time: p.date, value: p.positions_value }));

    const tradesData = clean.map((p) => {
      const hasBuy = (Number(p.buy_value_day) || 0) > 0;
      const hasSell = (Number(p.sell_value_day) || 0) > 0;

      let color = COLORS.tradeNone;
      let value = 0;

      if (hasBuy || hasSell) {
        value = 1;
        if (hasBuy && hasSell) color = COLORS.tradeBoth;
        else if (hasBuy) color = COLORS.tradeBuy;
        else if (hasSell) color = COLORS.tradeSell;
      }
      return { time: p.date, value, color };
    });

    posSeriesRef.current.setData(posData);
    tradesSeriesRef.current.setData(tradesData);

    if (!syncedTimeRange) chartRef.current.timeScale().fitContent();
  }, [data, todayPoint, syncedTimeRange, COLORS]);

  // apply synced range
  useEffect(() => {
    if (!chartRef.current || !syncedTimeRange) return;
    const ts = chartRef.current.timeScale();
    isSyncingRef.current = true;
    ts.setVisibleRange(syncedTimeRange);
    isSyncingRef.current = false;
  }, [syncedTimeRange]);

  const tip = pinned || hover;

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* ✅ title overlay */}
      {title ? (
        <div
          style={{
            position: "absolute",
            top: 6,
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: 12,
            fontWeight: 900,
            color: "rgba(255,255,255,0.88)",
            pointerEvents: "none",
            zIndex: 3,
            textShadow: "0 6px 18px rgba(0,0,0,0.55)",
          }}
        >
          {title}
        </div>
      ) : null}

      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {tip?.date ? (
        <div
          style={{
            position: "absolute",
            top: 28, // espaço pro título
            left: 8,
            padding: "8px 10px",
            fontSize: 11,
            background: COLORS.tooltipBg,
            color: COLORS.tooltipText,
            border: `1px solid ${COLORS.tooltipBorder}`,
            borderRadius: 12,
            boxShadow: "0 10px 26px rgba(0,0,0,0.35)",
            backdropFilter: "blur(8px)",
            pointerEvents: "none",
            lineHeight: 1.4,
            minWidth: 220,
            zIndex: 4,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 4 }}>
            {tip.date} {pinned ? <span style={{ opacity: 0.7 }}>(fixo)</span> : null}
          </div>

          <div>
            Posições: <b>{fmtMoney(tip.positions_value)}</b>
          </div>

          {(Number(tip.buy_value_day) > 0 || Number(tip.sell_value_day) > 0) && (
            <div style={{ marginTop: 4, opacity: 0.9 }}>
              Trades:{" "}
              <span style={{ opacity: 0.9 }}>
                buy {fmtMoney(tip.buy_value_day)} • sell {fmtMoney(tip.sell_value_day)}
              </span>
            </div>
          )}

          {pinned ? <div style={{ marginTop: 4, opacity: 0.7 }}>Toque fora do gráfico para soltar</div> : null}
        </div>
      ) : null}
    </div>
  );
}
